/**
 * Presence badge: shows whether the server is currently persisting
 * incoming screenshots to disk + DB. Driven by the `admin:presence`
 * Socket.IO event (broadcast on every flip) plus a local fallback
 * computed from the last heartbeat.
 *
 * Pill states:
 *   • "Đang lưu"        (green) — default, screenshots saved to disk
 *   • "Chỉ live stream" (amber) — admin clicked to disable saving
 *
 * Click to toggle persistence mode. The click is local until the server
 * confirms via the next socket push, but the server is fast so the lag
 * is imperceptible.
 */
import { useEffect, useState } from 'react'
import { HardDrive, Radio } from 'lucide-react'
import {
  getPersistenceMode,
  onPresenceChange,
  setPersistenceModeApi,
} from '@/services/admin-presence.service'
import { cn } from '@/lib/cn'

export function PresenceBadge() {
  const [online, setOnline] = useState<boolean | null>(null)
  const [mode, setMode] = useState<'live' | 'transient'>(() => getPersistenceMode())
  const [pending, setPending] = useState(false)

  useEffect(() => {
    const unsub = onPresenceChange((next, nextMode) => {
      setOnline(next)
      if (nextMode) setMode(nextMode)
    })
    return unsub
  }, [])

  async function handleClick() {
    if (pending) return
    const next = mode === 'live' ? 'transient' : 'live'
    setPending(true)
    // Optimistic update so the badge responds instantly.
    setMode(next)
    try {
      await setPersistenceModeApi(next)
    } catch {
      // Roll back on error.
      setMode(mode)
    } finally {
      setPending(false)
    }
  }

  // Unknown state (initial load before first heartbeat) → render a
  // muted "Đang kết nối" pill so the header doesn't flash empty.
  if (online === null) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
        <Radio className="h-3 w-3" />
        Đang kết nối
      </span>
    )
  }

  const saving = mode === 'live'

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={pending}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
        'disabled:opacity-60',
        saving
          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-950/60'
          : 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:hover:bg-amber-950/60',
      )}
      title={
        saving
          ? 'Đang lưu ảnh NV vào disk. Click để TẮT lưu (chỉ xem live, không tốn disk).'
          : 'Chỉ live stream — không lưu gì cả. Click để BẬT lưu ảnh.'
      }
    >
      {saving ? <HardDrive className="h-3 w-3" /> : <Radio className="h-3 w-3" />}
      {saving ? 'Đang lưu' : 'Chỉ live stream'}
    </button>
  )
}
